import React, { useState, useEffect, useRef, type FC } from 'react';
import { Linkedin, Mail } from 'lucide-react';
import {
  SETUP_FEE,
  MONTHLY_FEE_PROFESSIONAL,
  MONTHLY_FEE_PREMIUM,
  ADDON_SEAT_FEE,
  SEATS_PROFESSIONAL,
  SEATS_PREMIUM,
  MAX_SEATS_PER_INSTANCE,
  PRODUCT_NAME_MARKETING,
  TARIF_PROFESSIONAL,
  TARIF_PREMIUM,
  TARIF_ENTERPRISE,
  FEATURE_DIKTAT,
  FEATURE_LITIGATION,
} from '../../../constants';

/** Typografisch: nbsp nach § — verhindert visuelles „§9“-Zusammenkleben */
export const S9_RAO = '§\u00a09\u00a0RAO';

export const formatEur = (amount: number) =>
  new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);

export type PricingPackageId = 'setup' | 'professional' | 'premium' | 'enterprise';

export const PRICING_PACKAGES: {
  id: PricingPackageId;
  tarif?: string;
  price: number;
  priceLabel?: string;
  period: 'once' | 'month';
  name: string;
  hint: string;
  highlight?: boolean;
  audience: string;
  focus: string;
  includes: string[];
}[] = [
  {
    id: 'setup',
    price: SETUP_FEE,
    period: 'once',
    name: 'Setup & Infrastruktur',
    hint: 'Einrichtung Ihrer dedizierten Orbis-Umgebung (Kunden-Server, EU).',
    audience: 'Jede neue Kanzlei beim Start mit Orbis.',
    focus: 'Grundlage für Orbis Professional oder Premium — einmalig je Instanz.',
    includes: [
      'Dedizierter Kunden-Server (Edge), Self-Hosted in der EU',
      'n8n-Workflows, PocketBase-Mandantenstruktur, Nextcloud',
      'Technische Grundkonfiguration Ihrer Orbis-Instanz',
    ],
  },
  {
    id: 'professional',
    tarif: TARIF_PROFESSIONAL,
    price: MONTHLY_FEE_PROFESSIONAL,
    period: 'month',
    name: TARIF_PROFESSIONAL,
    hint: `Die Kanzlei-Plattform — ${SEATS_PROFESSIONAL} Sitze inklusive`,
    audience: 'Einzelanwälte und Kanzleien, die die volle Orbis-Basis wollen.',
    focus: `${PRODUCT_NAME_MARKETING} Basis — ${FEATURE_DIKTAT}, ARKO und BRAIN. Ohne ${FEATURE_LITIGATION}.`,
    includes: [
      `${FEATURE_DIKTAT}: Sprachnachricht → Aktenvermerk-PDF (Baseline)`,
      'KI-Rechtsassistent ARKO + BRAIN/LEXCORE (RIS-Rechtskern)',
      `${SEATS_PROFESSIONAL} Sitze inkl. · Zusatzsitz ${formatEur(ADDON_SEAT_FEE)}/Mon. (bis ${MAX_SEATS_PER_INSTANCE})`,
    ],
  },
  {
    id: 'premium',
    tarif: TARIF_PREMIUM,
    price: MONTHLY_FEE_PREMIUM,
    period: 'month',
    name: TARIF_PREMIUM,
    hint: `Alles aus Professional + ${FEATURE_LITIGATION}`,
    highlight: true,
    audience: 'Kanzleien, die zusätzlich strategische Schriftsatz-Analyse brauchen.',
    focus: `${PRODUCT_NAME_MARKETING} mit ${FEATURE_LITIGATION} — ${SEATS_PREMIUM} Sitze inklusive.`,
    includes: [
      'Voller Orbis-Professional-Umfang (Diktat, ARKO, BRAIN)',
      `${FEATURE_LITIGATION}: Schriftsatz → Strategie-PDF, doppelte Anonymisierung`,
      `${SEATS_PREMIUM} Sitze inkl. · Zusatzsitz ${formatEur(ADDON_SEAT_FEE)}/Mon. (bis ${MAX_SEATS_PER_INSTANCE})`,
    ],
  },
  {
    id: 'enterprise',
    tarif: TARIF_ENTERPRISE,
    price: 0,
    priceLabel: 'Individuell',
    period: 'month',
    name: TARIF_ENTERPRISE,
    hint: `Ab 21 Sitzen oder Sonderarchitektur`,
    audience: 'Große Kanzleien (>20 Sitze), zweite Instanz, upsized Dedicated, eigenes SLA.',
    focus: `Individuelle Architektur & SLA — jenseits von ${MAX_SEATS_PER_INSTANCE} Sitzen pro Instanz.`,
    includes: [
      `Mehr als ${MAX_SEATS_PER_INSTANCE} Sitze oder mehrere Orbis-Instanzen`,
      'Upsized Dedicated Server, individuelles Service-Level (SLA)',
      'Preis und Umfang nach individuellem Angebot',
    ],
  },
];

export const LEGAL_ENTITY = {
  NAME: 'Andreas Berger, Inhaber ARKORBIT AI Austria',
  BRAND: 'ARKORBIT AI',
  TYPE: 'Einzelunternehmer',
  OWNER: 'Andreas Berger',
  LOCATION: '3451 Michelhausen',
  ADDRESS: 'Grillenbergsiedlung 1/5/1',
  FN: 'Nicht im Firmenbuch eingetragen',
  UID: 'ATU83006408',
};

export const LINKEDIN_URL = 'https://www.linkedin.com/in/andreas-berger-b7b5943b5';
export const CONTACT_EMAIL = 'office@arkorbit.at';

/** 6 EU-Kanzleianforderungen — kompakte Badge-Grid für die Recht/Sicherheit-Sektion. */
export const LEGAL_COMPLIANCE: { label: string; sub: string }[] = [
  { label: S9_RAO, sub: 'Anwaltsgeheimnis' },
  { label: 'DSGVO Art. 28', sub: 'AVV inklusive' },
  { label: 'Self-Hosted', sub: 'EU · Hetzner Frankfurt' },
  { label: 'Kein CLOUD Act', sub: 'Keine US-Cloud' },
  { label: 'E2EE', sub: 'Matrix, Ende-zu-Ende' },
  { label: 'ISO 27001', sub: 'Hosting-Zertifizierung' },
];

export const AnimatedCounter: FC<{
  target: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
}> = ({ target, suffix = '', prefix = '', duration = 1800 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started.current) {
          started.current = true;
          const startTime = Date.now();
          const tick = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.5 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);
  return (
    <span ref={ref}>
      {prefix}
      {count}
      {suffix}
    </span>
  );
};

export type T04FooterProps = {
  onImpressumClick: () => void;
  onPrivacyClick: () => void;
  onAgbClick: () => void;
};

/** Dala-inspired footer: Copyright links, Nav mittig, Social rechts — ein Balken, schwarz. */
export const T04Footer: FC<T04FooterProps> = ({ onImpressumClick, onPrivacyClick, onAgbClick }) => (
  <footer className="t04-footer">
    <div className="t04-footer__inner">
      <p className="t04-footer__copyright">
        © {new Date().getFullYear()} {LEGAL_ENTITY.BRAND} Austria
        <span className="t04-footer__uid"> · UID {LEGAL_ENTITY.UID}</span>
      </p>

      <nav className="t04-footer__nav font-mono" aria-label="Rechtliches">
        <button type="button" onClick={onImpressumClick}>
          Impressum
        </button>
        <button type="button" onClick={onPrivacyClick}>
          Datenschutz
        </button>
        <button type="button" onClick={onAgbClick}>
          AGB &amp; AVV
        </button>
        <a href={`mailto:${CONTACT_EMAIL}`}>Kontakt</a>
      </nav>

      <div className="t04-footer__social">
        <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" aria-label="ARKORBIT auf LinkedIn">
          <Linkedin size={16} aria-hidden />
        </a>
        <a href={`mailto:${CONTACT_EMAIL}`} aria-label={`E-Mail an ${CONTACT_EMAIL}`}>
          <Mail size={16} aria-hidden />
        </a>
      </div>
    </div>
  </footer>
);
